import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { isSplineIframeElement } from '../../utils/splineCursor';
import './CustomCursor.css';

const TRAIL_COUNT = 4;
const INTERACTIVE_SELECTOR =
  'a, button, [role="button"], input, textarea, select, label, summary, [data-cursor-hover]';

const SHADOW_CURSOR_STYLE = '*, canvas { cursor: none !important; }';
const SPLINE_CURSOR_TARGETS =
  'spline-viewer, .hero-spline-inner canvas, .spline-viewer-host spline-viewer, .hero-spline-viewer-host spline-viewer';

const canUseCustomCursor = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  return (
    !window.matchMedia('(prefers-reduced-motion: reduce)').matches &&
    window.matchMedia('(hover: hover) and (pointer: fine)').matches
  );
};

const lerp = (start, end, amount) => start + (end - start) * amount;

const injectShadowCursorHide = (root) => {
  if (!root?.shadowRoot || root.shadowRoot.querySelector('[data-custom-cursor-style]')) {
    return;
  }

  const style = document.createElement('style');
  style.setAttribute('data-custom-cursor-style', 'true');
  style.textContent = SHADOW_CURSOR_STYLE;
  root.shadowRoot.appendChild(style);
};

const hideNativeCursor = () => {
  document.documentElement.style.setProperty('cursor', 'none', 'important');
  document.body.style.setProperty('cursor', 'none', 'important');

  document.querySelectorAll(SPLINE_CURSOR_TARGETS).forEach((element) => {
    if (element instanceof HTMLElement) {
      element.style.setProperty('cursor', 'none', 'important');
    }

    if (element instanceof HTMLElement && element.shadowRoot) {
      injectShadowCursorHide(element);
    }
  });
};

const CustomCursor = () => {
  const rootRef = useRef(null);
  const dotRef = useRef(null);
  const ringRef = useRef(null);
  const trailRefs = useRef([]);
  const mouseRef = useRef({ x: -100, y: -100 });
  const ringRefPos = useRef({ x: -100, y: -100 });
  const trailPositionsRef = useRef(
    Array.from({ length: TRAIL_COUNT }, () => ({ x: -100, y: -100 }))
  );
  const rafRef = useRef(null);
  const visibleRef = useRef(false);
  const hoveringRef = useRef(false);
  const [enabled] = useState(canUseCustomCursor);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    document.documentElement.classList.add('custom-cursor-active');
    hideNativeCursor();

    const cursorObserver = new MutationObserver(() => {
      hideNativeCursor();
    });

    cursorObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });

    let frameCount = 0;

    const setVisible = (isVisible) => {
      if (visibleRef.current === isVisible) return;
      visibleRef.current = isVisible;
      rootRef.current?.classList.toggle('custom-cursor--visible', isVisible);
    };

    const setHovering = (isHovering) => {
      if (hoveringRef.current === isHovering) return;
      hoveringRef.current = isHovering;
      rootRef.current?.classList.toggle('custom-cursor--hover', isHovering);
    };

    const updatePosition = (x, y) => {
      mouseRef.current = { x, y };

      if (dotRef.current) {
        dotRef.current.style.setProperty('--x', `${x}px`);
        dotRef.current.style.setProperty('--y', `${y}px`);
      }
    };

    const animate = () => {
      const { x: targetX, y: targetY } = mouseRef.current;
      const ring = ringRefPos.current;

      ring.x = lerp(ring.x, targetX, 0.14);
      ring.y = lerp(ring.y, targetY, 0.14);

      if (ringRef.current) {
        ringRef.current.style.setProperty('--x', `${ring.x}px`);
        ringRef.current.style.setProperty('--y', `${ring.y}px`);
      }

      const trails = trailPositionsRef.current;
      trails.forEach((trail, index) => {
        const followTarget = index === 0 ? mouseRef.current : trails[index - 1];
        const speed = 0.22 - index * 0.03;

        trail.x = lerp(trail.x, followTarget.x, speed);
        trail.y = lerp(trail.y, followTarget.y, speed);

        const trailEl = trailRefs.current[index];
        if (trailEl) {
          trailEl.style.setProperty('--x', `${trail.x}px`);
          trailEl.style.setProperty('--y', `${trail.y}px`);
        }
      });

      frameCount += 1;
      if (frameCount % 12 === 0) {
        hideNativeCursor();
      }

      rafRef.current = requestAnimationFrame(animate);
    };

    const handlePointerMove = (event) => {
      if (isSplineIframeElement(event.target)) {
        document.documentElement.classList.remove('custom-cursor-active');
        setVisible(false);
        setHovering(false);
        return;
      }

      if (!document.documentElement.classList.contains('custom-cursor-active')) {
        document.documentElement.classList.add('custom-cursor-active');
      }

      updatePosition(event.clientX, event.clientY);
      setVisible(true);
      hideNativeCursor();

      const target = event.target;
      if (target instanceof Element) {
        setHovering(Boolean(target.closest(INTERACTIVE_SELECTOR)));
      }
    };

    const handlePointerLeave = () => {
      setVisible(false);
      setHovering(false);
    };

    const handlePointerDown = () => {
      rootRef.current?.classList.add('custom-cursor--pressed');
    };

    const handlePointerUp = () => {
      rootRef.current?.classList.remove('custom-cursor--pressed');
    };

    document.addEventListener('pointermove', handlePointerMove, { passive: true, capture: true });
    document.documentElement.addEventListener('pointerleave', handlePointerLeave);
    document.addEventListener('pointerdown', handlePointerDown, { passive: true, capture: true });
    document.addEventListener('pointerup', handlePointerUp, { passive: true, capture: true });

    rafRef.current = requestAnimationFrame(animate);

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }

      cursorObserver.disconnect();
      document.removeEventListener('pointermove', handlePointerMove, { capture: true });
      document.documentElement.removeEventListener('pointerleave', handlePointerLeave);
      document.removeEventListener('pointerdown', handlePointerDown, { capture: true });
      document.removeEventListener('pointerup', handlePointerUp, { capture: true });
      document.documentElement.classList.remove('custom-cursor-active');
      document.documentElement.style.removeProperty('cursor');
      document.body.style.removeProperty('cursor');

      document.querySelectorAll(SPLINE_CURSOR_TARGETS).forEach((element) => {
        if (element instanceof HTMLElement) {
          element.style.removeProperty('cursor');
        }
      });
    };
  }, [enabled]);

  if (!enabled) {
    return null;
  }

  return createPortal(
    <div ref={rootRef} className="custom-cursor" aria-hidden="true">
      {Array.from({ length: TRAIL_COUNT }, (_, index) => (
        <span
          key={index}
          ref={(node) => {
            trailRefs.current[index] = node;
          }}
          className="custom-cursor__trail"
          style={{ '--trail-index': index }}
        />
      ))}
      <span ref={ringRef} className="custom-cursor__ring" />
      <span ref={dotRef} className="custom-cursor__dot" />
    </div>,
    document.body
  );
};

export default CustomCursor;
